import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient, rawRequest } from "@/api/client";
import type { components, paths } from "@/types/api";

// TODO: replace with generated type after openapi regen
type PropertyResponse = components["schemas"]["PropertyResponse"];

type CreateBody =
  paths["/api/v1/properties"]["post"]["requestBody"]["content"]["application/json"];
type UpdateBody =
  paths["/api/v1/properties/{property_id}"]["patch"]["requestBody"]["content"]["application/json"];

export const propertyKeys = {
  all: ["properties"] as const,
  list: (params?: Record<string, unknown>) =>
    ["properties", "list", params ?? {}] as const,
  detail: (id: string) => ["properties", "detail", id] as const,
  lookups: ["properties", "lookups"] as const,
};

export function useProperties(params?: { city?: string; published?: boolean }) {
  return useQuery({
    queryKey: propertyKeys.list(params),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/properties", {
        params: { query: params },
      });
      if (error || !data) throw new Error("Failed to load properties");
      return data;
    },
  });
}

export function useProperty(id: string | undefined) {
  return useQuery({
    queryKey: propertyKeys.detail(id ?? ""),
    enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/properties/{property_id}", {
        params: { path: { property_id: id! } },
      });
      if (error || !data) throw new Error("Failed to load property");
      return data;
    },
  });
}

export function usePublishReadiness(id: string | undefined) {
  return useQuery({
    queryKey: ["properties", "detail", id ?? "", "publish-readiness"],
    enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        "/api/v1/properties/{property_id}/publish-readiness",
        { params: { path: { property_id: id! } } },
      );
      if (error || !data) throw new Error("Failed to load the checklist");
      return data;
    },
  });
}

export function usePropertyLookups() {
  return useQuery({
    queryKey: propertyKeys.lookups,
    staleTime: 30 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/properties/lookups", {});
      if (error || !data) throw new Error("Failed to load lookups");
      return data;
    },
  });
}

export function useCreateProperty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateBody) => {
      const { data, error } = await apiClient.POST("/api/v1/properties", { body });
      if (error || !data) throw new Error("Failed to create property");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: propertyKeys.all }),
  });
}

export function useUpdateProperty(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateBody) => {
      const { data, error } = await apiClient.PATCH(
        "/api/v1/properties/{property_id}",
        { params: { path: { property_id: id } }, body },
      );
      if (error || !data) throw new Error("Failed to update property");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: propertyKeys.all }),
  });
}

type RoomBody =
  paths["/api/v1/properties/{property_id}/rooms"]["post"]["requestBody"]["content"]["application/json"];
type DescriptionBody =
  paths["/api/v1/properties/{property_id}/descriptions"]["put"]["requestBody"]["content"]["application/json"];

export function useAddRoom(propertyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: RoomBody) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/properties/{property_id}/rooms",
        { params: { path: { property_id: propertyId } }, body },
      );
      if (error || !data) throw new Error("Failed to add room");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: propertyKeys.detail(propertyId) }),
  });
}

export function useRemoveRoom(propertyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (roomId: string) => {
      const { error } = await apiClient.DELETE(
        "/api/v1/properties/{property_id}/rooms/{room_id}",
        { params: { path: { property_id: propertyId, room_id: roomId } } },
      );
      if (error) throw new Error("Failed to remove room");
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: propertyKeys.detail(propertyId) }),
  });
}

export function useUpsertDescription(propertyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: DescriptionBody) => {
      const { data, error } = await apiClient.PUT(
        "/api/v1/properties/{property_id}/descriptions",
        { params: { path: { property_id: propertyId } }, body },
      );
      if (error || !data) throw new Error("Failed to save description");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: propertyKeys.detail(propertyId) }),
  });
}

export function useSaveSelectionAdvice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { propertyId: string; advice: string }) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/properties/{property_id}/selection-advice",
        {
          params: { path: { property_id: input.propertyId } },
          body: { advice: input.advice },
        },
      );
      if (error || !data) throw new Error("Could not save the advice");
      return data;
    },
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: propertyKeys.detail(vars.propertyId) }),
  });
}

export function useClearSelectionAdvice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (propertyId: string) => {
      const { data, error } = await apiClient.DELETE(
        "/api/v1/properties/{property_id}/selection-advice",
        { params: { path: { property_id: propertyId } } },
      );
      if (error || !data) throw new Error("Could not clear the advice");
      return data;
    },
    onSuccess: (_d, propertyId) =>
      qc.invalidateQueries({ queryKey: propertyKeys.detail(propertyId) }),
  });
}

/** Puts a rented/ended property back on the market (rental_status → "ready"). */
export function useRelistProperty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (propertyId: string) =>
      // TODO: replace with typed apiClient.POST after openapi regen exposes
      // /api/v1/properties/{property_id}/relist.
      rawRequest<PropertyResponse>(
        "POST",
        `/api/v1/properties/${propertyId}/relist`,
      ),
    onSuccess: (_d, propertyId) => {
      qc.invalidateQueries({ queryKey: propertyKeys.detail(propertyId) });
      qc.invalidateQueries({ queryKey: propertyKeys.all });
    },
  });
}
